import { Injectable, Injector } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import {
  ActivityInterface,
  AutomationInterface,
  DidNotRunReason,
  EntityType,
  Stage,
  WorkflowStepInterface,
} from '@vendasta/automata';
import { Observable, of } from 'rxjs';
import { WORKFLOW_COMPONENTS } from '../../component-loader/definitions';
import { AutomationErrorHandlerService } from '../../services';
import { CommonDisplayActionService } from './common-display-action.service';
import { CommonDisplayActivityService } from './common-display-activity.service';
import { MultiBranchNodeData } from '../../../common/multi-node-branch-data';
import { getSingularEntityTypeDisplayTranslationString } from '../../../shared/entities';

@Injectable({ providedIn: 'root' })
export class DisplayService {
  emptyString = of('');

  constructor(
    private readonly translateService: TranslateService,
    public sanitizer: DomSanitizer,
    private injector: Injector,
    private readonly errorHandler: AutomationErrorHandlerService,
  ) {}

  getActionTitle(_namespace: string, step: WorkflowStepInterface): Observable<string> {
    if (!step) {
      return this.emptyString;
    }
    if (step.name) {
      return of(step.name);
    }
    const comp = WORKFLOW_COMPONENTS[step.taskDefinitionId];
    if (comp?.name) {
      return this.translateService.stream(comp.name);
    }
    return this.emptyString;
  }

  getActionSubtitle(
    namespace: string,
    taskDefinitionId: string,
    step: WorkflowStepInterface,
    automation: AutomationInterface,
    isWaitUntil?: boolean,
  ): Observable<string> {
    if (!step) {
      return this.emptyString;
    }
    const comp = WORKFLOW_COMPONENTS[taskDefinitionId];
    if (!comp) {
      return this.emptyString;
    }
    const service = this.injector.get<CommonDisplayActionService>(comp.actionDisplayService);
    if (service) {
      //TODO: is this specific to templates, or should this really be "is admin view"?
      if (automation.isTemplate) {
        return service.getTemplateActionSubtitle(namespace, step, isWaitUntil, automation);
      }
      return service.getActionSubtitle(namespace, step, isWaitUntil, automation);
    }
    return this.emptyString;
  }

  getActivityDisplayTitle(
    namespace: string,
    activity: ActivityInterface,
    automation: AutomationInterface,
  ): Observable<string> {
    if (!activity.completedProcessing) {
      return this.translateService.stream('AUTOMATIONS.ACTIVITY.PROCESSING.TITLE');
    }

    if (activity.stage !== Stage.STAGE_WORKFLOW_STEP) {
      switch (activity.stage) {
        case Stage.STAGE_WORKFLOW_COMPLETED:
          return this.translateService.stream('AUTOMATIONS.ACTIVITY.COMPLETED.TITLE');
        case Stage.STAGE_WORKFLOW_COMPLETED_FROM_GOAL:
          return this.translateService.stream('AUTOMATIONS.ACTIVITY.COMPLETED_FROM_GOAL.TITLE');
        case Stage.STAGE_WORKFLOW_CANCELED:
          return this.translateService.stream('AUTOMATIONS.ACTIVITY.CANCELED.TITLE');
        case Stage.STAGE_WORKFLOW_COMPLETED_FROM_ERROR:
          return this.translateService.stream('AUTOMATIONS.ACTIVITY.COMPLETED_FROM_ERROR.TITLE');
        case Stage.STAGE_WORKFLOW_DID_NOT_RUN:
          return this.translateService.stream('AUTOMATIONS.ACTIVITY.DID_NOT_RUN.TITLE');
        case Stage.STAGE_WORKFLOW_CONTINUED_FROM_ERROR:
          return this.translateService.stream('AUTOMATIONS.ACTIVITY.CONTINUED.CONTINUED_FROM_ERROR');
        case Stage.STAGE_WORKFLOW_CONTINUED:
          return this.translateService.stream('AUTOMATIONS.ACTIVITY.CONTINUED.CONTINUED');
        default:
          return this.emptyString;
      }
    }
    const comp = WORKFLOW_COMPONENTS[activity?.workflowStep?.taskDefinitionId];
    if (!comp) {
      return this.emptyString;
    }
    if (activity?.error) {
      return this.translateService.stream('AUTOMATIONS.ACTIVITY.ERROR_TITLE');
    }
    const service = this.injector.get<CommonDisplayActivityService>(comp.activityDisplayService);
    if (service) {
      return service.getActivityTitle(namespace, activity, automation);
    }
    return this.emptyString;
  }

  getActivityDisplaySubtitle(
    namespace: string,
    activity: ActivityInterface,
    automation: AutomationInterface,
    entityType: EntityType,
  ): Observable<string> {
    if (!activity.completedProcessing) {
      return of('');
    }

    if (activity.stage !== Stage.STAGE_WORKFLOW_STEP && activity.stage !== Stage.STAGE_WORKFLOW_CONTINUED_FROM_ERROR) {
      if (activity.stage === Stage.STAGE_WORKFLOW_CANCELED) {
        return this.translateService.stream('AUTOMATIONS.ACTIVITY.CANCELED.SUBTITLE_V2');
      }
      if (activity.stage === Stage.STAGE_WORKFLOW_COMPLETED_FROM_ERROR) {
        return this.translateService.stream('AUTOMATIONS.ACTIVITY.COMPLETED_FROM_ERROR.SUBTITLE');
      }
      if (activity.stage === Stage.STAGE_WORKFLOW_COMPLETED_FROM_GOAL && activity.goal) {
        return of(activity.goal.name || '');
      }
      if (activity.stage === Stage.STAGE_WORKFLOW_DID_NOT_RUN) {
        const activityData = activity?.activityData ? JSON.parse(activity.activityData) : {};
        switch (activityData.reason) {
          case DidNotRunReason.AutomationRunType:
            return this.translateService.stream('AUTOMATIONS.ACTIVITY.DID_NOT_RUN.SUBTITLE.RUN_TYPE_ONCE_ENTITY');
          case DidNotRunReason.AutomationOneAtATimeRunType:
            return this.translateService.stream(
              'AUTOMATIONS.ACTIVITY.DID_NOT_RUN.SUBTITLE.RUN_TYPE_ONE_AT_A_TIME_ENTITY',
            );
          case DidNotRunReason.RateLimited:
            return this.translateService.stream('AUTOMATIONS.ACTIVITY.DID_NOT_RUN.SUBTITLE.RATE_LIMITED');
          case DidNotRunReason.TriggerFiltersFailed:
            return this.translateService.stream('AUTOMATIONS.ACTIVITY.DID_NOT_RUN.SUBTITLE.TRIGGER_FILTERS', {
              entity: this.translateService
                .instant(getSingularEntityTypeDisplayTranslationString(entityType))
                .toLowerCase(),
            });
        }
      }
      return this.emptyString;
    }

    const comp = WORKFLOW_COMPONENTS[activity?.workflowStep?.taskDefinitionId];
    if (!comp) {
      return this.emptyString;
    }
    if (activity?.error) {
      return of(activity.error.humanReadableMessage);
    }
    const service = this.injector.get<CommonDisplayActivityService>(comp.activityDisplayService);
    if (service) {
      return service.getActivitySubtitle(namespace, activity, automation);
    }
    return this.emptyString;
  }

  getMultiBranchNodeData(
    namespace: string,
    taskDefinitionId: string,
    step: WorkflowStepInterface,
    automation: AutomationInterface,
    isWaitUntil?: boolean,
  ): Observable<MultiBranchNodeData[]> {
    if (!step) {
      return of([]);
    }
    this.errorHandler.deleteValidationError(step?.id);
    const comp = WORKFLOW_COMPONENTS[taskDefinitionId];
    if (!comp) {
      return of([]);
    }
    const service = this.injector.get<CommonDisplayActionService>(comp.actionDisplayService);
    if (!!service && comp.useMultiBranchingForNodeDisplay) {
      if (automation.isTemplate) {
        return service.getTemplateMultiBranchNodeData(namespace, step, automation, isWaitUntil);
      }
      return service.getMultiBranchNodeData(namespace, step, automation, isWaitUntil);
    }
    return of([]);
  }
}
